import type React from "react";
import {useCallback, useState} from "react";
import {Button, Stack, Typography} from "@mui/material";
import {Add} from "@mui/icons-material";
import {useOutletContext} from "react-router";
import {SegmentedControl} from "~/components/segmented-control/SegmentedControl";
import type {
    ComplianceRequirementFields,
    SupplierDetailOutletContext,
} from "~/pages/supplier-360/types/Supplier360Types";
import {useSupplierBuQualificationsQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useSupplierBuQualificationsQuery";
import {ComplianceRecordsTable} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceTable/ComplianceRecordsTable";
import {ComplianceBusinessUnitsTable} from "~/pages/supplier-360/tabs/SupplierCompliance/components/businessUnitsTable/ComplianceBusinessUnitsTable";
import {ComplianceForm} from "~/pages/supplier-360/tabs/SupplierCompliance/components/ComplianceForm";
import {ComplianceKpis} from "~/pages/supplier-360/tabs/SupplierCompliance/components/ComplianceKpis";
import {ComplianceBuKpis} from "~/pages/supplier-360/tabs/SupplierCompliance/components/businessUnitsTable/ComplianceBuKpis";

const VIEWS = ['Compliance', 'Business units'];

/** `null` = closed, `'add'` = new record, otherwise the record being edited. */
type DialogState = null | 'add' | ComplianceRequirementFields;

/**
 * The Compliance tab (§3.7): a "Compliance | Business units" switch over two tables, plus the
 * add / edit record dialog. `bu_qualification` is fetched once, unpaginated — it is joined onto
 * the records table and is the Business units table's row set.
 */
export const SupplierComplianceTab: React.FC = () => {
    const {entity} = useOutletContext<SupplierDetailOutletContext>();
    const [views, setViews] = useState(0);
    const [dialog, setDialog] = useState<DialogState>(null);

    const qualificationsQuery = useSupplierBuQualificationsQuery(entity.id);
    const qualifications = qualificationsQuery.data ?? [];
    const qualificationsLoading = qualificationsQuery.isFetching;

    const openEdit = useCallback((record: ComplianceRequirementFields) => setDialog(record), []);

    return (
        <Stack gap={2} sx={{mt: 2}}>
            {/* Each view has its own KPI strip: record states, or BU compliance statuses. */}
            {views === 0 ? <ComplianceKpis entityId={entity.id}/> : <ComplianceBuKpis entityId={entity.id}/>}

            <Stack direction="row" gap={2} sx={{alignItems: 'center'}}>
                <Typography variant="body2" fontWeight={600}>{views === 0 ? 'Compliance records' : 'Business units'}</Typography>
                <SegmentedControl labels={VIEWS} value={views} onChange={setViews}/>
                <Button
                    variant="contained"
                    startIcon={<Add/>}
                    onClick={() => setDialog('add')}
                    sx={{textTransform: 'none', ml: 'auto'}}
                >
                    Add compliance record
                </Button>
            </Stack>

            {views === 0
                ? <ComplianceRecordsTable entityId={entity.id} onEdit={openEdit}/>
                : (
                    <ComplianceBusinessUnitsTable
                        entityId={entity.id}
                        qualifications={qualifications}
                        loading={qualificationsLoading}
                    />
                )}

            {dialog !== null && (
                <ComplianceForm
                    entityId={entity.id}
                    qualificationsQuery={qualificationsQuery}
                    record={dialog === 'add' ? undefined : dialog}
                    onClose={() => setDialog(null)}
                />
            )}
        </Stack>
    );
};
